import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react'
import { Box } from '@mui/material'
import InvoicePDF from './InvoicePDF'
import { useLocation } from 'react-router-dom'
import Invoice from './Invoice'
import { getObject, getObjectsWithFilter, addObject, editObjectFilter } from '../../../api/apiClientCrud'
import TransformIcon from '@mui/icons-material/Transform'
import { toast } from 'react-toastify'
import { useAuth } from '../../../context/AuthContext'
import Toolbar from '../../toolbar/Toolbar'
import { useOutletContext } from 'react-router-dom'
import SettingsIcon from '@mui/icons-material/Settings'
import EuroIcon from '@mui/icons-material/Euro'
import EmailIcon from '@mui/icons-material/Email'
import { sendDocumentEmail } from '../../../utils/emailUtils'
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined'
import PDFReader from '../../pdfReader/PDFReader'
import {
  copyInvoice,
  createAdjustmentInvoice,
  createCreditNote,
  createEntity,
  fileToBase64,
  updateAttachments,
  updateDocumentStatus
} from '../../../utils/documentManager'
import useNavigate from '../../hooks/useNavigate'
import ActionMenu from '../../common/ActionMenu'
import StatusField from '../../fields/StatusField'
import Loading from '../../common/Loading'
import StatusDialog from '../../dialogs/StatusDialog'
import PaymentDialog from '../../dialogs/PaymentDialog'
import NotFound from '../../common/NotFound'
import DocumentPreview from '../../document/DocumentPreview'
import { pdf } from '@react-pdf/renderer'
import Income from '../Accounting/Income'
import { ContentCopy } from '@mui/icons-material'

const ViewInvoice = () => {
  const [invoice, setInvoice] = useState({})
  const [company, setCompany] = useState([])
  const [img, setImg] = useState('')
  const [showPdf, setShowPdf] = useState(false)
  const [statusList, setStatusList] = useState([])
  const location = useLocation()
  const { user } = useAuth()
  const userRight = useOutletContext()
  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false)
  const [isStatusChanged, setIsStatusChanged] = useState(false)
  const [files, setFiles] = useState([])
  const [settings, setSettings] = useState(null)
  const toastId = useRef(null)
  const history = useNavigate()
  const [anchorEl, setAnchorEl] = useState(null)
  const [loading, setLoading] = useState(true)
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false)
  const [pdfUrl, setPdfUrl] = useState('')

  const openMenu = useCallback((event) => setAnchorEl(event.currentTarget), [])
  const closeMenu = useCallback(() => setAnchorEl(null), [])

  const credit = useCallback(() => {
    createEntity({
      toast,
      toastIdRef: toastId,
      getObjectsWithFilter,
      addObject,
      editObjectFilter,
      history,
      user,
      entityType: 'creditNotes',
      settingsKey: 'creditNote_number',
      factoryFn: createCreditNote,
      args: [invoice],
      redirectPath: '/creditNotes/edit',
      successMessage: "L'avoir a été créé avec succès",
      updateHistoryState: { history: '/creditNotes/view' },
      onSuccess: () => {
        setInvoice((prev) => ({ ...prev, status: 81 }))
        setIsStatusChanged(true)
      }
    })
  }, [toast, history, user, invoice])

  const adjust = useCallback(() => {
    createEntity({
      toast,
      toastIdRef: toastId,
      getObjectsWithFilter,
      addObject,
      editObjectFilter,
      history,
      user,
      entityType: 'invoices',
      settingsKey: 'invoice_number',
      factoryFn: createAdjustmentInvoice,
      args: [invoice],
      redirectPath: '/invoices/edit',
      successMessage: 'La facture a été créée avec succès',
      updateHistoryState: { history: '/invoices/view' },
      onSuccess: () => {
        setInvoice((prev) => ({ ...prev, status: 82 }))
        setIsStatusChanged(true)
      }
    })
  }, [toast, history, user, invoice])

  const menu = useMemo(
    () => [
      { label: 'Créditer', onClick: credit },
      { label: 'Ajuster', onClick: adjust }
    ],
    [credit, adjust]
  )

  const sendEmail = useCallback(() => {
    try {
      sendDocumentEmail({
        type: 'facture',
        object: invoice,
        objectType: 'invoice',
        user,
        company,
        customer: invoice.customer,
        template: company.invoice_email_template,
        component: <InvoicePDF invoice_param={invoice} company_param={company} logo_param={img} settings={settings} />,
        setFiles,
        subjectPrefix: 'Votre facture',
        statusToUpdate: 70,
        updateStatus: (newStatus) => {
          setInvoice((prev) => ({ ...prev, status: newStatus }))
          setIsStatusChanged(true)
        }
      })
    } catch (error) {
      toast.error('Erreur lors de l’envoi de l’email')
    }
  }, [invoice, user, company, img, settings])

  const toolbarOptions = useMemo(
    () => ({
      objectType: 'invoices',
      title: () => {
        return `Facture ${invoice.invoice_number}`
      },
      accessRight: userRight,
      actions: {
        edit: true,
        editDisabled: () => invoice.status >= 70,
        files: true,
        back: true,
        audits: true,
        list: true
      },
      customActionsRight: [
        {
          icon: () => <PictureAsPdfOutlinedIcon />,
          tooltip: 'Afficher PDF',
          onClick: () => setShowPdf((prev) => !prev)
        },
        {
          icon: () => <EmailIcon />,
          tooltip: 'Envoyer par mail',
          onClick: () => sendEmail(),
          disabled: () => !invoice.customer?.email
        },
        {
          icon: () => <EuroIcon />,
          tooltip: 'Payer',
          onClick: () => setIsPaymentDialogOpen(true),
          disabled: () => invoice.status >= 80 || invoice.status < 70
        },
        {
          icon: () => <SettingsIcon />,
          tooltip: 'Changer statut',
          onClick: () => setIsStatusDialogOpen(true),
          disabled: () => invoice.status >= 80
        },
        {
          icon: () => <ContentCopy />,
          tooltip: 'Dupliquer',
          onClick: () => duplicate(),
          disabled: () => invoice.quote_id !== null || invoice.intervention_id !== null
        },
        {
          icon: () => <TransformIcon />,
          tooltip: 'Crediter/Ajuster',
          disabled: () => invoice.status < 70 || invoice.status > 80,
          onClick: (e) => openMenu(e)
        }
      ],
      customComponentsLeft: [
        {
          component: () => <StatusField value={invoice.status} type='invoices' statusList={statusList} />
        }
      ]
    }),
    [userRight, invoice, statusList, sendEmail, openMenu]
  )

  const duplicate = useCallback(() => {
    createEntity({
      toast,
      toastIdRef: toastId,
      getObjectsWithFilter,
      addObject,
      editObjectFilter,
      history,
      user,
      entityType: 'invoices',
      settingsKey: 'invoice_number',
      factoryFn: copyInvoice,
      args: [invoice],
      redirectPath: '/invoices/edit',
      successMessage: 'La facture a été dupliquée avec succès',
      updateHistoryState: { history: '/invoices/view' }
    })
  }, [toast, history, user, invoice])

  const closeStatusDialog = useCallback(
    (newValue) => {
      setIsStatusDialogOpen(false)
      if (newValue !== undefined && newValue !== invoice.status) {
        setInvoice((prevInvoice) => ({ ...prevInvoice, status: newValue }))
        setIsStatusChanged(true)
      }
    },
    [invoice.status]
  )

  const closePaymentDialog = useCallback(
    (newValue) => {
      setIsPaymentDialogOpen(false)
      if (newValue?.id !== undefined && newValue.id === invoice.id) {
        setInvoice((prev) => ({ ...prev, status: 80 }))
        setIsStatusChanged(true)
      }
    },
    [invoice.id]
  )

  useEffect(() => {
    if (!invoice?.id) return

    let url
    const generatePdfFromComponent = async () => {
      try {
        const attach = invoice.attachments?.find((f) => f.type === 'invoice')
        if (attach && attach.file?.data?.length) {
          const blob = new Blob([new Uint8Array(attach.file.data)], { type: 'application/pdf' })
          url = URL.createObjectURL(blob)
          setPdfUrl(url)
        } else {
          const component = (
            <InvoicePDF invoice_param={invoice} company_param={company} logo_param={img} settings={settings} />
          )
          const doc = pdf(component)
          const blob = await doc.toBlob()
          url = URL.createObjectURL(blob)
          setPdfUrl(url)
        }
      } catch (err) {}
    }

    generatePdfFromComponent()
    return () => {
      if (url) URL.revokeObjectURL(url)
    }
  }, [invoice, company, img, settings])

  useEffect(() => {
    if (!isStatusChanged) return
    updateDocumentStatus('invoices', invoice, user, toast, setIsStatusChanged)
  }, [isStatusChanged, invoice.id, user.email])

  useEffect(() => {
    setInvoice((prev) => ({
      ...prev,
      attachments: updateAttachments(prev.attachments, files)
    }))
  }, [files])

  useEffect(() => {
    const invoiceId = location?.state?.id
    if (!invoiceId) {
      toast.error('Facture introuvable.', { autoClose: 2000 })
      return
    }
    const fetchAll = async () => {
      try {
        const [statusRes, invoiceRes, companyRes, settingsRes] = await Promise.all([
          getObjectsWithFilter('status', { object: 'invoices' }),
          getObject('invoices', invoiceId),
          getObject('company', user.company_id),
          getObjectsWithFilter('settings', { company_id: user.company_id })
        ])

        setStatusList(statusRes.data)
        setInvoice(invoiceRes.data)
        setCompany(companyRes.data)
        setSettings(settingsRes.data)

        if (invoiceRes.data.attachments) {
          setFiles(invoiceRes.data.attachments)
          const attach = invoiceRes.data.attachments.find((f) => f.type === 'invoice')
          if (attach) {
            const blob = new Blob([new Uint8Array(attach.file.data)], { type: 'application/pdf' })
            const base64 = await fileToBase64(blob)
            setPdfUrl(`data:application/pdf;base64,${base64}`)
          }
        }

        const logoAttach = companyRes.data.attachments?.find((a) => a.type === 'logo')
        if (logoAttach) {
          const blob = new Blob([new Uint8Array(logoAttach.file.data)])
          const base64 = await fileToBase64(blob)
          setImg(base64)
        }
      } catch (error) {
        toast.error('Erreur lors du chargement des données', { autoClose: 2000 })
      } finally {
        setLoading(false)
      }
    }

    fetchAll()
  }, [location, user.company_id])

  if (loading)
    return (
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          height: '100%',
          minHeight: '100px',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Loading />
      </Box>
    )
  if (!invoice?.id) return <NotFound title='Facture introuvable' />

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 65px)', flexGrow: 1 }}>
      <Toolbar
        options={toolbarOptions}
        object={invoice}
        setObject={setFiles}
        setParentObject={setInvoice}
        parentObject={invoice}
      />
      {showPdf ? (
        <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
          <PDFReader url={pdfUrl} filename={`facture_${invoice?.invoice_number}.pdf`} />
        </Box>
      ) : (
        <Box sx={{ overflowY: 'auto', flexGrow: 1, paddingTop: '15px', paddingBottom: '15px' }}>
          <DocumentPreview Component={Invoice} document={invoice} />
        </Box>
      )}

      <StatusDialog
        open={isStatusDialogOpen}
        value={invoice.status}
        onClose={closeStatusDialog}
        type='invoices'
        statusList={statusList}
      />
      <PaymentDialog
        FormComponent={Income}
        defaultTitle='Payer facture'
        open={isPaymentDialogOpen}
        data={invoice}
        onClose={closePaymentDialog}
      />
      <ActionMenu anchorEl={anchorEl} onClose={closeMenu} menuItems={menu} />
    </Box>
  )
}

export default ViewInvoice
